import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getImageUrl } from '../utils/image';
import { hasCoordinates, parseCoordinate } from '../utils/coordinates';
import { useSearchParams } from 'react-router-dom';
import {
  formatStoredPhone,
  formatWhatsappInput,
  isCompletePhone,
  isMaskedPhoneInput,
  toStoredPhone,
} from '../utils/phone';
import { partnerApiService, referencesApiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { City, Category, CategoryGroup, PartnerFormData } from '../types/api';
import { ImageCropper } from './ImageCropper';
import { WorkingHoursEditor, WorkingHoursEditorHandle } from './WorkingHoursEditor';
import { TagsEditor, TagsEditorHandle } from './TagsEditor';
import { TaxonomyEditor, TaxonomySelection } from './TaxonomyEditor';
import { LegalDetailsEditor } from './LegalDetailsEditor';
import { FormShell, FormCard, FormFoot, FormTab } from './form/FormShell';
import { Field, FieldGrid } from './form/Field';
import { Toast, useToast } from './form/Toast';
import { MapPoint, PointPickerMap } from './map/PointPickerMap';
import { DEFAULT_COORDINATES } from '../config/constants';
import './PartnerForm.css';

interface PartnerFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

type SectionKey = 'general' | 'location' | 'photos' | 'tags' | 'contacts' | 'hours' | 'legal';

const SECTION_KEYS: SectionKey[] = ['general', 'location', 'photos', 'tags', 'contacts', 'hours', 'legal'];

const MAP_POINT_REQUIRED = 'Поставьте точку заведения на карте — без неё гости не видят расстояние и маршрут.';

const SECTION_TITLES: Record<SectionKey, { label: string; hint: string }> = {
  general: { label: 'Общее', hint: 'Как бизнес выглядит в приложении для клиентов.' },
  location: { label: 'Расположение', hint: 'Адрес и точка на карте — по ним клиент находит вас.' },
  photos: { label: 'Фотографии', hint: 'Главное фото и до трёх дополнительных для галереи.' },
  tags: {
    label: 'Теги',
    hint: 'Тематические маркеры: по ним вас находят в поиске — «Хэллоуин», «Живая музыка», «Веганское».',
  },
  contacts: { label: 'Контакты', hint: 'Ссылки, по которым клиент сможет с вами связаться.' },
  hours: { label: 'Часы работы', hint: 'Когда вы открыты и когда принимаете клиентов.' },
  legal: { label: 'Реквизиты', hint: 'Необязательно: для договора и закрывающих документов.' },
};

// Подсказка под полем — формат, в котором контакт откроется у клиента по
// нажатию (QA 45.2): плейсхолдер пропадает с первой буквой, подсказка — нет.
const CONTACT_FIELDS: Array<{ name: keyof PartnerFormData; label: string; placeholder: string; hint: string }> = [
  {
    name: 'whatsapp',
    label: 'WhatsApp',
    placeholder: '+7 (777) 000-00-00',
    hint: 'Номер в формате +7 (7XX) XXX-XX-XX',
  },
  { name: 'instagram', label: 'Instagram', placeholder: 'username', hint: 'Логин без @ или ссылка на профиль' },
  {
    name: 'facebook',
    label: 'Facebook',
    placeholder: 'https://facebook.com/…',
    hint: 'Ссылка на страницу, например https://facebook.com/…',
  },
  {
    name: 'youtube',
    label: 'YouTube',
    placeholder: 'https://youtube.com/…',
    hint: 'Ссылка на страницу, например https://youtube.com/…',
  },
  {
    name: 'linkedin',
    label: 'LinkedIn',
    placeholder: 'https://linkedin.com/…',
    hint: 'Ссылка на страницу, например https://linkedin.com/…',
  },
  { name: 'website', label: 'Веб-сайт', placeholder: 'https://example.com', hint: 'Полный адрес с https://' },
];

const WHATSAPP_INCOMPLETE = 'Номер неполный: нужно 11 цифр, например +7 (701) 123-45-67';

const MAX_ADDITIONAL_IMAGES = 3;

export function PartnerForm({ onSuccess, onCancel }: PartnerFormProps) {
  const { partner, updatePartner, loadPartnerProfile } = useAuth();
  // Раздел можно открыть ссылкой (`/edit?section=location`) — так ведёт
  // чек-лист запуска, когда не хватает точки на карте.
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState<SectionKey>(() => {
    const requested = searchParams.get('section') as SectionKey | null;
    return requested && SECTION_KEYS.includes(requested) ? requested : 'general';
  });
  const [mapFocusKey, setMapFocusKey] = useState(0);
  const [isLocatingAddress, setIsLocatingAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useToast();
  const [cities, setCities] = useState<City[]>([]);
  const [categoryGroups, setCategoryGroups] = useState<CategoryGroup[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [mainImagePreview, setMainImagePreview] = useState<string | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [rawImage, setRawImage] = useState<string | null>(null);
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState<string[]>([]);
  const [pendingAdditionalFiles, setPendingAdditionalFiles] = useState<File[]>([]);
  const [showAdditionalCropper, setShowAdditionalCropper] = useState(false);
  const [rawAdditionalImage, setRawAdditionalImage] = useState<string | null>(null);
  const [currentAdditionalIndex, setCurrentAdditionalIndex] = useState<number>(0);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const workingHoursRef = useRef<WorkingHoursEditorHandle>(null);
  const tagsRef = useRef<TagsEditorHandle>(null);
  // Защита от перезаписи введённых данных авто-загрузкой профиля (visibility change,
  // focus, периодический рефреш в AuthContext). Заполняем форму partner-ом ровно один раз.
  const isFormInitialized = useRef(false);

  const [formData, setFormData] = useState<PartnerFormData>({
    name: '',
    address: '',
    description: '',
    longitude: undefined,
    latitude: undefined,
    city_id: 0,
    primary_category_id: null,
    categories: [],
    subcategories: [],
    whatsapp: '',
    instagram: '',
    youtube: '',
    facebook: '',
    linkedin: '',
    website: '',
  });

  // Снимок последнего сохранённого состояния. Расписание и теги ведут свой
  // dirty сами — сюда попадает только то, чем управляет эта форма.
  const [savedSnapshot, setSavedSnapshot] = useState('');
  // WhatsApp в том виде, в каком он уже сохранён. Номер, заведённый до маски
  // в другом формате, не должен мешать сохранить остальной профиль.
  const savedWhatsapp = useRef('');
  const [whatsappError, setWhatsappError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        await loadPartnerProfile();
        const [citiesData, groupsData, categoriesData] = await Promise.all([
          referencesApiService.getCities(),
          // Дерево обязательно: по нему считаются допустимые специализации.
          referencesApiService.getCategoryGroups(),
          // Плоский список нужен только категориям вне разделов («Другое»).
          // Не загрузился — выбор по разделам работает и без него.
          referencesApiService.getCategories().catch((categoriesError) => {
            console.error('Не загрузился плоский список категорий:', categoriesError);
            return [] as Category[];
          }),
        ]);
        setCities(citiesData);
        setCategoryGroups(groupsData);
        setCategories(categoriesData);
      } catch (err: any) {
        setError('Ошибка загрузки данных');
      } finally {
        setIsLoadingData(false);
      }
    };
    loadData();
  }, [loadPartnerProfile]);

  useEffect(() => {
    if (!partner || isFormInitialized.current) return;
    isFormInitialized.current = true;

    const initial: PartnerFormData = {
      name: partner.name || '',
      address: partner.address || '',
      description: partner.description || '',
      // API отдаёт координаты строкой — без приведения точка считалась бы
      // отсутствующей, хотя сохранена.
      longitude: parseCoordinate(partner.longitude),
      latitude: parseCoordinate(partner.latitude),
      city_id: partner.city?.id || 0,
      // primary_category приходит отдельным полем, а в categories он же лежит
      // с is_primary — в форме держим их врозь, чтобы не отправить дублем.
      primary_category_id: partner.primary_category?.id ?? null,
      categories: (partner.categories || [])
        .filter((c) => c.id !== partner.primary_category?.id)
        .map((c) => c.id),
      subcategories: partner.subcategories?.map((s) => s.id) || [],
      whatsapp: formatStoredPhone(partner.whatsapp),
      instagram: partner.instagram || '',
      youtube: partner.youtube || '',
      facebook: partner.facebook || '',
      linkedin: partner.linkedin || '',
      website: partner.website || '',
    };

    setFormData(initial);
    setSavedSnapshot(JSON.stringify(initial));
    savedWhatsapp.current = initial.whatsapp || '';

    if (partner.main_image) {
      setMainImagePreview(getImageUrl(partner.main_image.url) || null);
    }
    if (partner.cover_image) {
      setCoverImagePreview(getImageUrl(partner.cover_image.url) || null);
    }
    if (partner.additional_images && partner.additional_images.length > 0) {
      setAdditionalImagePreviews(
        partner.additional_images.map((img) => getImageUrl(img.url)).filter((url): url is string => !!url)
      );
    }
  }, [partner]);

  const isDirty = useMemo(
    () => savedSnapshot !== '' && JSON.stringify(formData) !== savedSnapshot,
    [formData, savedSnapshot]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Точка обязательна (ADR-014): без неё клиент видел расстояние до 0°/0°
    // («9 000 км»), а заведение не запустить.
    if (!hasCoordinates(formData.latitude, formData.longitude)) {
      setActiveSection('location');
      setToast({ message: MAP_POINT_REQUIRED, tone: 'error' });
      return;
    }

    const whatsapp = (formData.whatsapp || '').trim();
    if (
      whatsapp &&
      whatsapp !== savedWhatsapp.current &&
      isMaskedPhoneInput(whatsapp) &&
      !isCompletePhone(whatsapp)
    ) {
      setWhatsappError(WHATSAPP_INCOMPLETE);
      setActiveSection('contacts');
      setToast({ message: WHATSAPP_INCOMPLETE, tone: 'error' });
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Расписание уходит до профиля: ответ updateProfile обновляет partner и
      // переинициализирует редактор часов, несохранённые дни при этом теряются.
      const hoursResult = workingHoursRef.current
        ? await workingHoursRef.current.saveAllDirty()
        : { saved: 0, failed: 0 };

      const response = await partnerApiService.updateProfile({
        ...formData,
        whatsapp: toStoredPhone(formData.whatsapp || ''),
      });
      if (response && response.partner) {
        updatePartner(response.partner);
      }

      // Sync тегов, если они изменены
      const tagsResult = tagsRef.current ? await tagsRef.current.saveIfDirty() : { saved: false };

      await loadPartnerProfile();
      setSavedSnapshot(JSON.stringify(formData));
      savedWhatsapp.current = formData.whatsapp || '';

      const issues: string[] = [];
      if (hoursResult.failed > 0) issues.push(`${hoursResult.failed} дн. расписания`);
      if (tagsResult.error) issues.push('теги');

      if (issues.length > 0) {
        setToast({ message: `Сохранено с ошибками: ${issues.join(', ')}`, tone: 'error' });
      } else if (!mainImagePreview) {
        // Фото не обязательно для сохранения — но без него карточка в
        // приложении выглядит пустой, поэтому напоминаем прямо после успеха.
        setToast({ message: 'Сохранено. Добавьте главное фото — без него карточка пустая', tone: 'success' });
      } else {
        const parts: string[] = ['профиль'];
        if (hoursResult.saved > 0) parts.push(`${hoursResult.saved} дн. расписания`);
        if (tagsResult.saved) parts.push('теги');
        setToast({ message: `Сохранено: ${parts.join(', ')}`, tone: 'success' });
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Не удалось сохранить';
      setError(msg);
      setToast({ message: msg, tone: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'whatsapp') setWhatsappError('');
    setFormData((prev) => {
      if (name === 'latitude' || name === 'longitude') {
        return { ...prev, [name]: parseCoordinate(value) };
      }
      if (name === 'whatsapp') {
        return { ...prev, whatsapp: formatWhatsappInput(value, prev.whatsapp || '') };
      }
      return { ...prev, [name]: value };
    });
  };

  // Центр города заведения: на нём открывается карта, пока точки нет.
  // city_id из <select> приходит строкой, поэтому сравниваем как числа.
  const cityMapCenter = (): MapPoint | null => {
    const city = cities.find((c) => c.id === Number(formData.city_id));
    const latitude = parseCoordinate(city?.latitude);
    const longitude = parseCoordinate(city?.longitude);
    return latitude !== undefined && longitude !== undefined ? { latitude, longitude } : null;
  };

  /**
   * Черновик точки по введённому адресу (ADR-014): сервер ищет его в городе
   * заведения, точка встаёт на карту, партнёр проверяет и двигает её.
   * Геокодер в Казахстане часто находит только улицу — об этом и говорим.
   *
   * Адрес ищется только по этой кнопке. Подсказок при вводе нет намеренно:
   * правила публичного Nominatim запрещают автодополнение (раздел
   * «Unacceptable Use»), а бан закрыл бы геокодер всему сервису.
   */
  const handleFindOnMap = async () => {
    const address = formData.address.trim();
    if (address.length < 3) {
      setToast({ message: 'Сначала введите адрес заведения', tone: 'error' });
      return;
    }
    setIsLocatingAddress(true);
    try {
      const [found] = await partnerApiService.geocodeAddress(address, Number(formData.city_id) || null);
      if (!found) {
        setToast({ message: 'Адрес не найден — поставьте точку кликом по карте', tone: 'error' });
        return;
      }
      setFormData((prev) => ({ ...prev, latitude: found.latitude, longitude: found.longitude }));
      setMapFocusKey((key) => key + 1);
      setToast({
        message:
          found.precision === 'house'
            ? 'Точка поставлена по адресу — проверьте, что она на здании заведения'
            : 'Нашли только улицу — передвиньте точку на здание заведения',
        tone: 'success',
      });
    } catch {
      setToast({ message: 'Поиск не ответил — поставьте точку кликом по карте', tone: 'error' });
    } finally {
      setIsLocatingAddress(false);
    }
  };

  /**
   * Адрес по точке — тоже у нашего сервера (ADR-025), подпись короткая:
   * улица, дом, город. Кнопку партнёр жмёт сам, поэтому найденное заменяет
   * введённый адрес всегда, даже одна улица. В анкете адрес подставляется без
   * кнопки, и правило там мягче (`addressByPoint`).
   */
  const handleReverseGeocode = async () => {
    if (formData.latitude === undefined || formData.longitude === undefined) {
      setToast({ message: 'Сначала выберите точку на карте', tone: 'error' });
      return;
    }
    setIsReverseGeocoding(true);
    try {
      const found = await partnerApiService.reverseGeocode(formData.latitude, formData.longitude);
      const address = found?.label.trim();
      if (address) {
        setFormData((prev) => ({ ...prev, address }));
      } else {
        setToast({ message: 'Не удалось определить адрес для выбранной точки', tone: 'error' });
      }
    } catch (geocodeError) {
      console.error('Ошибка обратного геокодирования:', geocodeError);
      setToast({ message: 'Не удалось определить адрес', tone: 'error' });
    } finally {
      setIsReverseGeocoding(false);
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setToast({ message: 'Браузер не поддерживает геолокацию', tone: 'error' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData((prev) => ({
          ...prev,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }));
        setMapFocusKey((key) => key + 1);
      },
      (geoError) => {
        console.error('Ошибка получения геолокации:', geoError);
        setToast({ message: 'Не удалось определить ваше местоположение', tone: 'error' });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleTaxonomyChange = (next: TaxonomySelection) => {
    setFormData((prev) => ({ ...prev, ...next }));
  };

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setRawImage(ev.target?.result as string);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (croppedBlob: Blob) => {
    setShowCropper(false);
    setMainImagePreview(URL.createObjectURL(croppedBlob));
    setFormData((prev) => ({
      ...prev,
      main_image: new File([croppedBlob], 'main_image.jpg', { type: 'image/jpeg' }),
    }));
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setRawImage(null);
  };

  const handleRemoveMainImage = async () => {
    if (partner?.main_image && getImageUrl(partner.main_image.url) === mainImagePreview) {
      try {
        if (window.confirm('Вы уверены, что хотите удалить основное изображение?')) {
          await partnerApiService.deleteImage(partner.main_image.id);
          await loadPartnerProfile();
        } else {
          return;
        }
      } catch (removeError) {
        console.error('Ошибка при удалении изображения:', removeError);
        setToast({ message: 'Не удалось удалить изображение', tone: 'error' });
        return;
      }
    }
    setMainImagePreview(null);
    setFormData((prev) => ({ ...prev, main_image: null }));
  };

  /**
   * Обложка идёт без кроппера: кадр широкий, обрезать его квадратной рамкой
   * главного фото нельзя — ради этого поле и заводили (БП 5.2).
   */
  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setCoverImagePreview(URL.createObjectURL(file));
    setFormData((prev) => ({ ...prev, cover_image: file }));
  };

  const handleRemoveCoverImage = async () => {
    if (partner?.cover_image && getImageUrl(partner.cover_image.url) === coverImagePreview) {
      if (!window.confirm('Удалить обложку?')) return;
      try {
        await partnerApiService.deleteImage(partner.cover_image.id);
        await loadPartnerProfile();
      } catch (removeError) {
        console.error('Ошибка при удалении обложки:', removeError);
        setToast({ message: 'Не удалось удалить обложку', tone: 'error' });
        return;
      }
    }
    setCoverImagePreview(null);
    setFormData((prev) => ({ ...prev, cover_image: null }));
  };

  const handleAdditionalImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (files.length > 0) {
      setPendingAdditionalFiles(files);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setRawAdditionalImage(ev.target?.result as string);
        setShowAdditionalCropper(true);
        setCurrentAdditionalIndex(0);
      };
      reader.readAsDataURL(files[0]);
    }
  };

  const handleAdditionalCropComplete = (croppedBlob: Blob) => {
    setShowAdditionalCropper(false);
    setAdditionalImagePreviews((prev) => [...prev, URL.createObjectURL(croppedBlob)]);
    setFormData((prev) => ({
      ...prev,
      additional_images: [
        ...(prev.additional_images || []),
        new File([croppedBlob], `additional_${Date.now()}.jpg`, { type: 'image/jpeg' }),
      ],
    }));
    const nextIndex = currentAdditionalIndex + 1;
    if (pendingAdditionalFiles[nextIndex]) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setRawAdditionalImage(ev.target?.result as string);
        setShowAdditionalCropper(true);
        setCurrentAdditionalIndex(nextIndex);
      };
      reader.readAsDataURL(pendingAdditionalFiles[nextIndex]);
    } else {
      setPendingAdditionalFiles([]);
      setRawAdditionalImage(null);
      setCurrentAdditionalIndex(0);
    }
  };

  const handleAdditionalCropCancel = () => {
    setShowAdditionalCropper(false);
    setPendingAdditionalFiles([]);
    setRawAdditionalImage(null);
    setCurrentAdditionalIndex(0);
  };

  const handleRemoveAdditionalImage = async (index: number) => {
    const urlToRemove = additionalImagePreviews[index];
    const existingImage = partner?.additional_images?.find((img) => getImageUrl(img.url) === urlToRemove);

    if (existingImage) {
      try {
        if (window.confirm('Вы уверены, что хотите удалить это изображение?')) {
          await partnerApiService.deleteImage(existingImage.id);
          await loadPartnerProfile();
          setAdditionalImagePreviews((prev) => prev.filter((_, i) => i !== index));
        }
      } catch (removeError) {
        console.error('Ошибка при удалении изображения:', removeError);
        setToast({ message: 'Не удалось удалить изображение', tone: 'error' });
      }
    } else {
      setAdditionalImagePreviews((prev) => prev.filter((_, i) => i !== index));
      const currentPreviews = additionalImagePreviews;
      const isExisting = (url: string) => partner?.additional_images?.some((img) => getImageUrl(img.url) === url);
      let newImageIndex = 0;
      for (let i = 0; i < index; i++) {
        if (!isExisting(currentPreviews[i])) newImageIndex++;
      }
      setFormData((prev) => ({
        ...prev,
        additional_images: prev.additional_images?.filter((_, i) => i !== newImageIndex) || [],
      }));
    }
  };

  const handlePickOnMap = (point: MapPoint) => {
    setFormData((prev) => ({ ...prev, latitude: point.latitude, longitude: point.longitude }));
  };

  if (isLoadingData) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
        <div className="m3-spinner" role="status" aria-label="Загрузка" />
      </div>
    );
  }

  const contactsCount = CONTACT_FIELDS.filter(
    (field) => ((formData[field.name] as string) || '').trim().length > 0
  ).length;
  const photosCount =
    (mainImagePreview ? 1 : 0) + (coverImagePreview ? 1 : 0) + additionalImagePreviews.length;
  const generalCount = [
    formData.name.trim(),
    formData.city_id ? 'city' : '',
    formData.description?.trim(),
    formData.primary_category_id ? 'category' : '',
  ].filter(Boolean).length;
  const hasPoint = hasCoordinates(formData.latitude, formData.longitude);
  const locationCount = [formData.address.trim(), hasPoint ? 'geo' : ''].filter(Boolean).length;
  // Без точки карта открывается на городе заведения, а не на Астане по
  // умолчанию: партнёру из Алматы не нужно листать карту через полстраны.
  const cityCenter = cityMapCenter();
  const mapPoint: MapPoint | null = hasPoint ? { latitude: formData.latitude!, longitude: formData.longitude! } : null;
  const mapCenter: MapPoint = mapPoint ?? cityCenter ?? DEFAULT_COORDINATES;
  const mapZoom = hasPoint ? 17 : 12;

  const tabs: FormTab[] = [
    { key: 'general', label: SECTION_TITLES.general.label, meta: `${generalCount}/4` },
    { key: 'location', label: SECTION_TITLES.location.label, meta: `${locationCount}/2` },
    { key: 'photos', label: SECTION_TITLES.photos.label, meta: `${photosCount}/${MAX_ADDITIONAL_IMAGES + 1}` },
    { key: 'tags', label: SECTION_TITLES.tags.label, meta: String(partner?.tags?.length ?? 0) },
    { key: 'contacts', label: SECTION_TITLES.contacts.label, meta: `${contactsCount}/${CONTACT_FIELDS.length}` },
    { key: 'hours', label: SECTION_TITLES.hours.label, meta: `${partner?.working_hours?.length ?? 0}/7` },
    // Без счётчика: «0/4» у необязательного раздела читается как недоделка.
    { key: 'legal', label: SECTION_TITLES.legal.label },
  ];

  const saveLabel = isLoading ? 'Сохранение…' : 'Сохранить';
  // У реквизитов своя кнопка и свой запрос: «Сохранить» профиля и его статус
  // черновика на этой вкладке врали бы — они про остальные разделы.
  const isLegalSection = activeSection === 'legal';

  return (
    <>
      <FormShell
        eyebrow={`Профиль${partner?.name ? ` · ${partner.name}` : ''}`}
        title="Редактирование"
        isDirty={isLegalSection ? undefined : isDirty}
        tabs={tabs}
        activeTab={activeSection}
        onTabChange={(key) => setActiveSection(key as SectionKey)}
        tabsLabel="Разделы профиля"
        actions={
          <>
            <button type="button" className="qp-btn qp-btn--outline" onClick={onCancel} disabled={isLoading}>
              Отменить
            </button>
            {!isLegalSection && (
              <button type="submit" form="partner-form" className="qp-btn qp-btn--primary" disabled={isLoading}>
                {saveLabel}
              </button>
            )}
          </>
        }
        footer={
          isLegalSection ? undefined : (
            <FormFoot note="Изменения видны клиентам сразу после сохранения.">
              <button type="submit" form="partner-form" className="qp-btn qp-btn--primary" disabled={isLoading}>
                {saveLabel}
              </button>
            </FormFoot>
          )
        }
      >
        {error && (
          <div className="qp-notice qp-notice--error" role="alert">
            {error}
          </div>
        )}

        <form id="partner-form" onSubmit={handleSubmit} className="qp-stack" noValidate>
          {activeSection === 'general' && (
            <FormCard title={SECTION_TITLES.general.label} hint={SECTION_TITLES.general.hint}>
              <FieldGrid>
                <Field label="Название бизнеса" required htmlFor="partner-name">
                  <input
                    id="partner-name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="qp-control"
                    placeholder="Как вас найдут в приложении"
                    disabled={isLoading}
                  />
                </Field>

                <Field label="Город" required htmlFor="partner-city">
                  <select
                    id="partner-city"
                    name="city_id"
                    value={formData.city_id}
                    onChange={handleInputChange}
                    className="qp-control"
                    disabled={isLoading}
                  >
                    <option value={0}>Выберите город</option>
                    {cities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field
                  label="Описание"
                  wide
                  htmlFor="partner-description"
                  hint="Без цен и телефонов — они берутся из других полей."
                >
                  <textarea
                    id="partner-description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={6}
                    className="qp-control"
                    placeholder="Чем вы отличаетесь от соседей по улице"
                    disabled={isLoading}
                  />
                </Field>

                <div className="qp-field qp-field--wide">
                  <TaxonomyEditor
                    groups={categoryGroups}
                    categories={categories}
                    value={{
                      primary_category_id: formData.primary_category_id,
                      categories: formData.categories,
                      subcategories: formData.subcategories,
                    }}
                    onChange={handleTaxonomyChange}
                    disabled={isLoading}
                  />
                </div>
              </FieldGrid>
            </FormCard>
          )}

          {activeSection === 'location' && (
            <FormCard title={SECTION_TITLES.location.label} hint={SECTION_TITLES.location.hint}>
              <FieldGrid>
                <Field label="Адрес" required wide htmlFor="partner-address">
                  <input
                    id="partner-address"
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="qp-control"
                    placeholder="Улица, дом"
                    disabled={isLoading}
                    autoComplete="off"
                  />
                </Field>

                <div className="qp-field qp-field--wide">
                  <div className="qp-chips">
                    <button
                      type="button"
                      onClick={handleFindOnMap}
                      disabled={isLoading || isLocatingAddress || formData.address.trim().length < 3}
                      className="qp-btn qp-btn--primary qp-btn--sm"
                    >
                      {isLocatingAddress ? 'Ищем…' : 'Найти адрес на карте'}
                    </button>
                    <button
                      type="button"
                      onClick={handleReverseGeocode}
                      disabled={isLoading || isReverseGeocoding || !hasPoint}
                      className="qp-btn qp-btn--outline qp-btn--sm"
                    >
                      {isReverseGeocoding ? 'Определяем…' : 'Адрес по точке на карте'}
                    </button>
                    <button
                      type="button"
                      onClick={handleUseMyLocation}
                      className="qp-btn qp-btn--outline qp-btn--sm"
                      disabled={isLoading}
                    >
                      Моё местоположение
                    </button>
                  </div>
                </div>

                <div className={`qp-field qp-field--wide${hasPoint ? '' : ' qp-field--invalid'}`}>
                  <span className="qp-field-label">
                    Точка на карте
                    <span className="qp-field-req" aria-hidden>
                      {' '}
                      *
                    </span>
                  </span>
                  <PointPickerMap
                    point={mapPoint}
                    center={mapCenter}
                    zoom={mapZoom}
                    focusKey={mapFocusKey}
                    onPick={handlePickOnMap}
                    testId="partner-map"
                  />
                  {hasPoint ? (
                    <span className="qp-field-hint">
                      {`Выбрано: ${formData.latitude!.toFixed(6)}, ${formData.longitude!.toFixed(6)}. `}
                      Точку можно перетащить на здание заведения.
                    </span>
                  ) : (
                    <span className="qp-field-error" role="alert">
                      Точка обязательна: по ней клиент видит расстояние и строит маршрут. Нажмите «Найти адрес на
                      карте» или кликните по карте.
                    </span>
                  )}
                </div>

                <Field label="Широта" htmlFor="partner-latitude">
                  <input
                    id="partner-latitude"
                    name="latitude"
                    type="number"
                    step="any"
                    value={formData.latitude ?? ''}
                    onChange={handleInputChange}
                    onBlur={() => setMapFocusKey((key) => key + 1)}
                    className="qp-control qp-control--num"
                    disabled={isLoading}
                  />
                </Field>

                <Field label="Долгота" htmlFor="partner-longitude">
                  <input
                    id="partner-longitude"
                    name="longitude"
                    type="number"
                    step="any"
                    value={formData.longitude ?? ''}
                    onChange={handleInputChange}
                    onBlur={() => setMapFocusKey((key) => key + 1)}
                    className="qp-control qp-control--num"
                    disabled={isLoading}
                  />
                </Field>
              </FieldGrid>
            </FormCard>
          )}

          {activeSection === 'photos' && (
            <FormCard title={SECTION_TITLES.photos.label} hint={SECTION_TITLES.photos.hint}>
              <div className="qp-form-group">
                {/*
                  Без звёздочки: сервер фото не требует ни при сохранении, ни
                  при активации заведения, а форма из-за неё не давала сохранить
                  вообще ничего, пока не загрузишь картинку.
                */}
                <span className="qp-field-label">Главное фото</span>
                {mainImagePreview ? (
                  <div className="qp-shot qp-shot--cover">
                    <img src={mainImagePreview} alt="Главное фото" />
                    <div className="qp-shot-actions">
                      <label className="qp-shot-btn" htmlFor="main-image-input">
                        Заменить
                      </label>
                      <button
                        type="button"
                        className="qp-shot-btn qp-shot-btn--icon"
                        onClick={handleRemoveMainImage}
                        aria-label="Удалить главное фото"
                        disabled={isLoading}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
                          <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="qp-drop" htmlFor="main-image-input">
                    <span className="qp-drop-label">Перетащите файл или</span>
                    <span className="qp-btn qp-btn--outline">Выбрать файл</span>
                    <span className="qp-drop-note">PNG или JPG до 10 МБ · лучше 1200×800</span>
                  </label>
                )}
                <input
                  id="main-image-input"
                  type="file"
                  accept="image/*"
                  onChange={handleMainImageChange}
                  disabled={isLoading}
                  style={{ display: 'none' }}
                />
              </div>

              <div className="qp-form-group">
                <span className="qp-field-label">Обложка</span>
                {coverImagePreview ? (
                  <div className="qp-shot qp-shot--cover">
                    <img src={coverImagePreview} alt="Обложка" />
                    <div className="qp-shot-actions">
                      <label className="qp-shot-btn" htmlFor="cover-image-input">
                        Заменить
                      </label>
                      <button
                        type="button"
                        className="qp-shot-btn qp-shot-btn--icon"
                        onClick={handleRemoveCoverImage}
                        aria-label="Удалить обложку"
                        disabled={isLoading}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
                          <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="qp-drop" htmlFor="cover-image-input">
                    <span className="qp-drop-label">Широкий кадр для шапки</span>
                    <span className="qp-btn qp-btn--outline">Выбрать файл</span>
                    <span className="qp-drop-note">PNG или JPG до 4 МБ · лучше 1600×420</span>
                  </label>
                )}
                <input
                  id="cover-image-input"
                  type="file"
                  accept="image/*"
                  onChange={handleCoverImageChange}
                  disabled={isLoading}
                  style={{ display: 'none' }}
                />
              </div>

              <div className="qp-form-group">
                <span className="qp-field-label">
                  Галерея · {additionalImagePreviews.length} из {MAX_ADDITIONAL_IMAGES}
                </span>
                <div className="qp-tiles">
                  {additionalImagePreviews.map((src, index) => (
                    <div key={index} className="qp-shot qp-shot--tile">
                      <img src={src} alt={`Фото ${index + 1}`} />
                      <div className="qp-shot-actions">
                        <button
                          type="button"
                          className="qp-shot-btn qp-shot-btn--icon"
                          onClick={() => handleRemoveAdditionalImage(index)}
                          aria-label={`Удалить фото ${index + 1}`}
                          disabled={isLoading}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
                            <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  {additionalImagePreviews.length < MAX_ADDITIONAL_IMAGES && (
                    <button
                      type="button"
                      className="qp-tile-add"
                      onClick={() => document.getElementById('additional-image-input')?.click()}
                      disabled={isLoading}
                      aria-label="Добавить фото"
                    >
                      +
                    </button>
                  )}
                </div>
                <input
                  id="additional-image-input"
                  type="file"
                  accept="image/*"
                  onChange={handleAdditionalImagesChange}
                  disabled={isLoading || additionalImagePreviews.length >= MAX_ADDITIONAL_IMAGES}
                  style={{ display: 'none' }}
                />
              </div>
            </FormCard>
          )}

          {/* Теги и расписание всегда смонтированы: у них свой dirty state,
              и переключение раздела не должно его терять. */}
          <div style={{ display: activeSection === 'tags' ? 'block' : 'none' }}>
            <FormCard title={SECTION_TITLES.tags.label} hint={SECTION_TITLES.tags.hint}>
              <TagsEditor ref={tagsRef} initialTags={partner?.tags} disabled={isLoading} />
            </FormCard>
          </div>

          {activeSection === 'contacts' && (
            <FormCard title={SECTION_TITLES.contacts.label} hint={SECTION_TITLES.contacts.hint}>
              <FieldGrid>
                {CONTACT_FIELDS.map((field) => (
                  <Field
                    key={field.name}
                    label={field.label}
                    htmlFor={`partner-${field.name}`}
                    hint={field.hint}
                    error={field.name === 'whatsapp' ? whatsappError : undefined}
                  >
                    <input
                      id={`partner-${field.name}`}
                      name={field.name}
                      type={field.name === 'website' ? 'url' : field.name === 'whatsapp' ? 'tel' : 'text'}
                      inputMode={field.name === 'whatsapp' ? 'numeric' : undefined}
                      autoComplete={field.name === 'whatsapp' ? 'tel' : undefined}
                      value={(formData[field.name] as string) || ''}
                      onChange={handleInputChange}
                      className="qp-control"
                      placeholder={field.placeholder}
                      disabled={isLoading}
                      aria-invalid={field.name === 'whatsapp' && whatsappError ? true : undefined}
                    />
                  </Field>
                ))}
              </FieldGrid>
            </FormCard>
          )}

          <div style={{ display: activeSection === 'hours' ? 'block' : 'none' }}>
            <FormCard title={SECTION_TITLES.hours.label} hint={SECTION_TITLES.hours.hint}>
              <WorkingHoursEditor ref={workingHoursRef} initialHours={partner?.working_hours} />
            </FormCard>
          </div>
        </form>

        {/* Вне `partner-form`: формы не вкладываются. Скрыт, а не размонтирован —
            как теги и расписание: ввод переживает переключение разделов. */}
        <div className="qp-stack" style={isLegalSection ? undefined : { display: 'none' }}>
          <LegalDetailsEditor legal={partner?.legal ?? null} />
        </div>
      </FormShell>

      {showCropper && rawImage && (
        <ImageCropper image={rawImage} onCropComplete={handleCropComplete} onCancel={handleCropCancel} aspect={1} />
      )}
      {showAdditionalCropper && rawAdditionalImage && (
        <ImageCropper
          image={rawAdditionalImage}
          onCropComplete={handleAdditionalCropComplete}
          onCancel={handleAdditionalCropCancel}
          aspect={1}
        />
      )}

      <Toast toast={toast} />
    </>
  );
}
